import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import sessionStorage from 'redux-persist/lib/storage/session';
import { RootState } from '../store';

const MAX_TABS = 10;

export interface ITab {
  title: string;
  route: string;
  replaceIfNotExist?: boolean;
  shouldCloseDuplicate?: boolean;
}

interface ITabsState {
  enabled: boolean;
  selectedTabIndex: number;
  tabs: ITab[];
  closedTab: ITab;
  recentlyClosedTabs: ITab[];
}

const initialState: ITabsState = {
  enabled: true,
  selectedTabIndex: 0,
  tabs: [],
  closedTab: {} as ITab,
  recentlyClosedTabs: [],
};

export const getNextTabIndex = (selectedTabIndex: number, tabs: ITab[], tabIndex: number) => {
  const closingLastTab = tabIndex === tabs.length;
  const isLastTabSelected = selectedTabIndex === tabs.length;

  if (closingLastTab && isLastTabSelected) {
    return tabs.length - 1;
  } else if (tabIndex < selectedTabIndex) {
    return selectedTabIndex - 1;
  }

  return selectedTabIndex;
};

const normalizeRoute = (input: string) => {
  try {
    const url = new URL(input, window.location.origin);

    // Normalize pathname (remove trailing slash)
    const pathname = url.pathname.replace(/\/+$/, '');

    // Normalize query params (sorted)
    const sortedParams = new URLSearchParams();
    [...url.searchParams.entries()]
      .sort(([aKey], [bKey]) => aKey.localeCompare(bKey))
      .forEach(([key, value]) => {
        if (value !== null) sortedParams.append(key, value);
      });

    return sortedParams.toString() ? `${pathname}?${sortedParams.toString()}` : pathname;
  } catch (err) {
    console.warn('Failed to normalize route:', input);
    return input;
  }
};

const addToRecentlyClosed = (state: ITabsState, closedTabs: ITab[]) => {
  const openRoutes = new Set(state.tabs.map((t) => normalizeRoute(t.route)));

  const filtered = state.recentlyClosedTabs.filter(
    (t) =>
      !closedTabs.some((closed) => normalizeRoute(t.route) === normalizeRoute(closed.route)) &&
      !openRoutes.has(normalizeRoute(t.route))
  );

  state.recentlyClosedTabs = [...closedTabs, ...filtered];
};

export const tabsSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    setEnabled: (state, action: PayloadAction<boolean>) => {
      state.enabled = action.payload;

      if (!action.payload) {
        state.tabs = [];
      }
    },
    selectTab: (state, action: PayloadAction<number>) => {
      state.selectedTabIndex = action.payload;
    },
    openTab: (state, action: PayloadAction<ITab>) => {
      const { route, title, replaceIfNotExist, shouldCloseDuplicate } = action.payload;
      const normalizedRoute = normalizeRoute(route);

      state.recentlyClosedTabs = state.recentlyClosedTabs.filter((t) => normalizeRoute(t.route) !== normalizedRoute);

      const existingIndex = state.tabs.findIndex((tab) => {
        return normalizeRoute(tab.route) === normalizedRoute;
      });

      if (state.closedTab && normalizeRoute(state.closedTab.route) === normalizedRoute) {
        const [mostRecent] = state.recentlyClosedTabs;
        state.closedTab = mostRecent || ({} as ITab);
      }

      if (existingIndex >= 0) {
        if (existingIndex !== state.selectedTabIndex) {
          if (shouldCloseDuplicate) {
            // Close the current tab only if shouldCloseDuplicate is true
            state.tabs.splice(state.selectedTabIndex, 1);
            // Adjust existingIndex if current tab was before it
            const adjustedIndex = state.selectedTabIndex < existingIndex ? existingIndex - 1 : existingIndex;
            state.selectedTabIndex = adjustedIndex;
          } else {
            // Just select the existing tab without closing current
            state.selectedTabIndex = existingIndex;
          }
        } else {
          // Just select it
          state.selectedTabIndex = existingIndex;
        }
        return;
      }

      if (replaceIfNotExist && state.tabs.length > 0) {
        // Replace current selected tab
        state.tabs[state.selectedTabIndex] = {
          title,
          route
        };

        return;
      }

      if (state.tabs.length === MAX_TABS) {
        // Replace current tab if limit reached
        state.tabs[state.selectedTabIndex] = {
          title,
          route
        };

        return;
      }
      // Add new tab
      state.tabs = state.enabled ? [...state.tabs, { title, route }] : [{ title, route }];
      state.selectedTabIndex = state.tabs.length - 1;
    },
    updateCurrentTab: (state, action: PayloadAction<Partial<ITab>>) => {
      const { title, route } = action.payload;

      if (state.tabs[state.selectedTabIndex]) {
        state.tabs[state.selectedTabIndex].title = title || state.tabs[state.selectedTabIndex].title;
        state.tabs[state.selectedTabIndex].route = route || state.tabs[state.selectedTabIndex].route;
      } else {
        state.tabs.push({
          title: title || '',
          route: route || '',
        });
      }
    },
    closeTab: (state, action: PayloadAction<number>) => {
      const closedTab = state.tabs[action.payload];
      if (closedTab) {
        addToRecentlyClosed(state, [closedTab]);
      }

      state.tabs.splice(action.payload, 1);
      state.closedTab = closedTab;
      state.selectedTabIndex = getNextTabIndex(state.selectedTabIndex, state.tabs, action.payload);
    },
    closeLeftTabs: (state, action: PayloadAction<number>) => {
      const closedTabs = state.tabs.slice(0, action.payload);
      if (closedTabs.length) {
        addToRecentlyClosed(state, closedTabs);
      }

      state.tabs.splice(0, action.payload);

      if (state.selectedTabIndex < action.payload || state.selectedTabIndex === action.payload) {
        state.selectedTabIndex = 0;
      } else {
        state.selectedTabIndex = state.selectedTabIndex - action.payload;
      }
    },
    closeRightTabs: (state, action: PayloadAction<number>) => {
      const closedTabs = state.tabs.slice(action.payload + 1);
      if (closedTabs.length) {
        addToRecentlyClosed(state, closedTabs);
      }

      state.tabs.splice(action.payload + 1, state.tabs.length - 1);

      if (state.selectedTabIndex > action.payload) {
        state.selectedTabIndex = state.tabs.length - 1;
      }
    },
    closeAllTabs: (state) => {
      state.tabs.length = 0;
      state.selectedTabIndex = 0;
    },
    setClosedTab: (state, action: PayloadAction<ITab>) => {
      const closedTab = action.payload;
      state.closedTab = closedTab;

      if (closedTab) {
        addToRecentlyClosed(state, [closedTab]);
      }
    },
    openTabFromRecentlyClosed: (state, action: PayloadAction<number>) => {
      const tabToReopen = state.recentlyClosedTabs[action.payload];
      if (tabToReopen) {
        state.tabs.push(tabToReopen);
        state.selectedTabIndex = state.tabs.length - 1;
        state.recentlyClosedTabs.splice(action.payload, 1);
      }
    },
    clearRecentlyClosedTabs: (state) => {
      state.recentlyClosedTabs = [];
    },
    setTabs: (state, action: PayloadAction<ITab[]>) => {
      state.tabs = action.payload;
    },
  },
});

export const {
  openTab,
  closeTab,
  closeLeftTabs,
  closeRightTabs,
  closeAllTabs,
  selectTab,
  setEnabled,
  updateCurrentTab,
  setClosedTab,
  setTabs,
  clearRecentlyClosedTabs,
} = tabsSlice.actions;

export const getSelectTabsEnabled = (state: RootState) => state.tabs.enabled;
export const getSelectActiveTabIndex = (state: RootState) => state.tabs.selectedTabIndex;
export const getTabs = (state: RootState) => state.tabs.tabs;
export const getRecentlyClosedTabs = (state: RootState) => state.tabs.recentlyClosedTabs;
export const getActiveTab = (state: RootState) => state.tabs.tabs[state.tabs.selectedTabIndex] || {};
export const getClosedTab = (state: RootState) => state.tabs.closedTab;

export const tabsPersistConfig = {
  key: 'tabs',
  storage: sessionStorage,
};
